import {observer} from 'mobx-react-lite';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {
  EDynamicTooltip,
  EIconButton,
  EUndecoratedButton,
} from 'webapp-lib/Components';

import {ProgressBar, SimplePlaceholder} from '#';
import {Close, Sync} from '_/assets/images';
import {DocumentFileUpload} from '_/store/DocumentFileUpload';
import {DocumentFileUploadsByState} from '_/store/DocumentUpload.store';
import {getRouteUrl} from '_/utilities';

import {
  AllItemsActionWrapper,
  CasusAndFileNameWrapper,
  PendingUploadQueueItem,
  StyledUploadQueue,
  UploadQueueFileName,
  UploadQueueItem,
  UploadQueueItemCasus,
} from './UploadQueue.styles';

const CasusAndFileName = ({
  fileName,
  casusDisplayNameWithClient,
  linkToDocument,
  closePopup,
}: {
  fileName: string;
  casusDisplayNameWithClient: string | undefined;
  linkToDocument?: string;
  closePopup?: () => void;
}) => (
  <CasusAndFileNameWrapper>
    <EDynamicTooltip title={casusDisplayNameWithClient}>
      <UploadQueueItemCasus>{casusDisplayNameWithClient}</UploadQueueItemCasus>
    </EDynamicTooltip>
    <EDynamicTooltip title={fileName}>
      <UploadQueueFileName>
        {linkToDocument ? (
          <Link to={linkToDocument} onClick={closePopup}>
            {fileName}
          </Link>
        ) : (
          fileName
        )}
      </UploadQueueFileName>
    </EDynamicTooltip>
  </CasusAndFileNameWrapper>
);

const UPendingItemList = ({
  items,
  abortAll,
}: {
  items: DocumentFileUpload[];
  abortAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return <SimplePlaceholder customText={t('uploadQueue.noPendingItems')} />;
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={abortAll}>
          {t('uploadQueue.abortAllPendingItems')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map(
          ({id, name, progress, abort, casusDisplayNameWithClient}) => (
            <PendingUploadQueueItem key={id}>
              <div>
                <CasusAndFileName
                  casusDisplayNameWithClient={casusDisplayNameWithClient}
                  fileName={name}
                />
                <EIconButton onClick={abort}>
                  <Close />
                </EIconButton>
              </div>
              <ProgressBar showActions={false} value={progress} />
            </PendingUploadQueueItem>
          )
        )}
      </StyledUploadQueue>
    </>
  );
};
const PendingItemList = observer(UPendingItemList);

const USuccessfulItemList = ({
  items,
  closePopup,
  clearAll,
}: {
  items: DocumentFileUpload[];
  closePopup: () => void;
  clearAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return (
      <SimplePlaceholder customText={t('uploadQueue.noSuccessfulItems')} />
    );
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={clearAll}>
          {t('uploadQueue.clearAll')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map(
          ({
            id,
            name,
            casusDisplayNameWithClient,
            casusId,
            createdDocumentId,
            folderId,
          }) => (
            <UploadQueueItem key={id}>
              <CasusAndFileName
                casusDisplayNameWithClient={casusDisplayNameWithClient}
                closePopup={closePopup}
                fileName={name}
                linkToDocument={getRouteUrl({
                  module: 'casus-documents',
                  casusId,
                  folderId,
                  documentId: createdDocumentId,
                  clearFilters: true,
                })}
              />
            </UploadQueueItem>
          )
        )}
      </StyledUploadQueue>
    </>
  );
};
const SuccessfulItemList = observer(USuccessfulItemList);

const UFailedItemList = ({
  items,
  retry,
  clearAll,
}: {
  items: DocumentFileUpload[];
  retry: (documentFileUpload: DocumentFileUpload) => void;
  clearAll: () => void;
}) => {
  const {t} = useTranslation('documents');

  if (items.length === 0) {
    return <SimplePlaceholder customText={t('uploadQueue.noFailedItems')} />;
  }

  return (
    <>
      <AllItemsActionWrapper>
        <EUndecoratedButton onClick={clearAll}>
          {t('uploadQueue.clearAll')}
        </EUndecoratedButton>
      </AllItemsActionWrapper>
      <StyledUploadQueue>
        {items.map((documentFileUpload) => (
          <UploadQueueItem key={documentFileUpload.id}>
            <CasusAndFileName
              casusDisplayNameWithClient={
                documentFileUpload.casusDisplayNameWithClient
              }
              fileName={documentFileUpload.name}
            />
            <EIconButton onClick={() => retry(documentFileUpload)}>
              <Sync />
            </EIconButton>
          </UploadQueueItem>
        ))}
      </StyledUploadQueue>
    </>
  );
};
const FailedItemList = observer(UFailedItemList);

const UUploadQueueLists = ({
  activeTabItem,
  items,
  closePopup,
  clearAllSuccessfulFiles,
  clearAllFailedFiles,
  abortAllPendingFiles,
  retryUpload,
}: {
  activeTabItem: 'PENDING' | 'SUCCESSFUL' | 'FAILED';
  items: DocumentFileUploadsByState;
  closePopup: () => void;
  clearAllSuccessfulFiles: () => void;
  clearAllFailedFiles: () => void;
  abortAllPendingFiles: () => void;
  retryUpload: (documentFileUpload: DocumentFileUpload) => void;
}) => {
  switch (activeTabItem) {
    case 'PENDING':
      return (
        <PendingItemList
          abortAll={abortAllPendingFiles}
          items={items.pending}
        />
      );
    case 'SUCCESSFUL':
      return (
        <SuccessfulItemList
          clearAll={clearAllSuccessfulFiles}
          closePopup={closePopup}
          items={items.successful}
        />
      );
    case 'FAILED':
      return (
        <FailedItemList
          clearAll={clearAllFailedFiles}
          items={items.failed}
          retry={retryUpload}
        />
      );
  }
};
export const UploadQueueLists = observer(UUploadQueueLists);
